import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

// Mirrors `button.tsx` but for icon-only buttons: square geometry, the SVG size
// is driven by the `size` variant, and an `aria-label` is required (there is no
// visible text to name the control).
const iconButtonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        ghost: 'text-foreground hover:bg-[var(--ghost-hover-bg)]',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        filled:
          'bg-primary text-primary-foreground hover:bg-[var(--primary-hover)]',
      },
      size: {
        sm: 'size-8 [&_svg]:size-4',
        md: 'size-10 [&_svg]:size-5',
        lg: 'size-12 [&_svg]:size-6',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'md',
    },
  }
)

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  /** Required — an icon-only button has no text, so it must be labelled. */
  'aria-label': string
  asChild?: boolean
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(iconButtonVariants({ variant, size }), className)}
        ref={ref}
        // Default to a non-submitting button so an IconButton inside a <form>
        // (e.g. a composer) never triggers an accidental submit.
        type={asChild ? undefined : (type ?? 'button')}
        {...props}
      />
    )
  }
)
IconButton.displayName = 'IconButton'

export { IconButton, iconButtonVariants }
